/**
 * Telegram **public** mode (sources.md "telegram"): a public channel's web preview `https://t.me/s/<name>`,
 * no account. `relay-only` (no CORS); 20 posts per page, `?before=<id>` for older ones. Parsed with
 * linkedom (lazily imported). External id `<name>:<id>`. Cursor `{ lastId }` — the newest post id seen.
 */

import type { MediaItem, RawItem, SourceFetchContext, SourceFetchResult, SourceResolution, SourceRuntime } from '../../contracts/sources.js';
import { DigesterError } from '../../kernel/errors.js';
import { assertOk, invalidInput, parseDate } from '../http.js';

export const TME = 'https://t.me';
const USERNAME = /^[A-Za-z][A-Za-z0-9_]{3,31}$/;
/** Posts per t.me/s page (sources.md). */
const PAGE_POSTS = 20;

export function parsePublicAddress(address: string): string | null {
  let raw = address.trim();
  raw = raw.replace(/^https?:\/\//i, '').replace(/^(www\.)?(t\.me|telegram\.me)\//i, '').replace(/^s\//, '').replace(/^@/, '');
  raw = raw.split(/[/?#]/)[0] ?? '';
  return USERNAME.test(raw) ? raw : null;
}

export const publicPageUrl = (name: string, before?: number): string => `${TME}/s/${name}${before !== undefined ? `?before=${before}` : ''}`;

type Element = {
  getAttribute(name: string): string | null;
  querySelector(selector: string): Element | null;
  querySelectorAll(selector: string): Iterable<Element>;
  replaceWith(...nodes: unknown[]): void;
  remove(): void;
  textContent: string | null;
  innerHTML: string;
  ownerDocument: { createTextNode(text: string): unknown };
  classList: { contains(name: string): boolean };
};

async function parseDocument(html: string): Promise<Element> {
  const { parseHTML } = await import('linkedom');
  return parseHTML(html).document as unknown as Element;
}

function backgroundUrl(style: string | null): string | null {
  const m = /background-image:\s*url\(['"]?([^'")]+)['"]?\)/i.exec(style ?? '');
  if (!m?.[1]) return null;
  const url = m[1].startsWith('//') ? `https:${m[1]}` : m[1];
  return /^https?:\/\//i.test(url) ? url : null;
}

/** Text block HTML with Telegram's emoji wrappers and click handlers reduced to plain content. */
function cleanTextHtml(block: Element): string {
  for (const emoji of [...block.querySelectorAll('i.emoji')]) emoji.replaceWith(block.ownerDocument.createTextNode(emoji.textContent ?? ''));
  for (const wrap of [...block.querySelectorAll('tg-emoji')]) wrap.replaceWith(block.ownerDocument.createTextNode(wrap.textContent ?? ''));
  return block.innerHTML.trim();
}

const placeholder = (media: readonly MediaItem[]): string => {
  const first = media[0];
  if (!first) return '';
  return first.type === 'image' ? '[Photo]' : first.type === 'video' ? '[Video]' : first.type === 'audio' ? '[Audio]' : '[Link]';
};

export interface PublicPage {
  title: string;
  posts: Array<RawItem & { id: number }>;
  /** Id for the next older page (`?before=`), if any. */
  before: number | null;
}

/** Parse one t.me/s page. */
export async function parsePublicPage(html: string, name: string): Promise<PublicPage> {
  const doc = await parseDocument(html);
  const title = doc.querySelector('meta[property="og:title"]')?.getAttribute('content')?.trim() ?? '';
  const posts: PublicPage['posts'] = [];
  for (const node of doc.querySelectorAll('.tgme_widget_message[data-post]')) {
    if (node.classList.contains('service_message')) continue;
    const post = node.getAttribute('data-post') ?? '';
    const id = Number(post.split('/')[1]);
    if (!Number.isInteger(id) || id <= 0) continue;
    const media: MediaItem[] = [];
    const url = `${TME}/${name}/${id}`;
    for (const photo of node.querySelectorAll('.tgme_widget_message_photo_wrap')) {
      const src = backgroundUrl(photo.getAttribute('style'));
      if (src) media.push({ type: 'image', url: src });
    }
    for (const video of node.querySelectorAll('.tgme_widget_message_video_player')) {
      const thumb = backgroundUrl(video.querySelector('.tgme_widget_message_video_thumb')?.getAttribute('style') ?? null);
      const href = video.getAttribute('href');
      media.push({ type: 'video', url: href && /^https?:\/\//.test(href) ? href : url, ...(thumb ? { thumbnailUrl: thumb } : {}) });
    }
    const preview = node.querySelector('a.tgme_widget_message_link_preview');
    const previewHref = preview?.getAttribute('href');
    if (preview && previewHref && /^https?:\/\//i.test(previewHref)) {
      const t = preview.querySelector('.link_preview_title')?.textContent?.trim();
      const thumb = backgroundUrl(preview.querySelector('.link_preview_right_image, .link_preview_image')?.getAttribute('style') ?? null);
      media.push({ type: 'link', url: previewHref, ...(t ? { title: t } : {}), ...(thumb ? { thumbnailUrl: thumb } : {}) });
    }
    const textBlock = node.querySelector('.tgme_widget_message_text');
    const html = textBlock ? cleanTextHtml(textBlock) : '';
    const item: RawItem & { id: number } = { id, externalId: `${name}:${id}`, url };
    if (html) item.html = html;
    else if (media.length > 0) item.text = placeholder(media);
    else continue; // nothing we can show (unsupported post type)
    const date = parseDate(node.querySelector('.tgme_widget_message_date time')?.getAttribute('datetime') ?? null);
    if (date !== undefined) item.publishedAt = date;
    const author = node.querySelector('.tgme_widget_message_from_author')?.textContent?.trim() || node.querySelector('.tgme_widget_message_owner_name')?.textContent?.trim();
    if (author) item.author = author;
    if (media.length > 0) item.media = media;
    const image = media.find((m) => m.type === 'image')?.url ?? media.find((m) => m.thumbnailUrl)?.thumbnailUrl;
    if (image) item.imageUrl = image;
    item.extra = { media: media.map((m) => (m.type === 'image' ? 'photo' : m.type)) };
    posts.push(item);
  }
  const moreBefore = doc.querySelector('.tme_messages_more[data-before]')?.getAttribute('data-before');
  const before = moreBefore && /^\d+$/.test(moreBefore) ? Number(moreBefore) : null;
  return { title, posts, before };
}

async function getPage(runtime: SourceRuntime, name: string, before?: number) {
  const url = publicPageUrl(name, before);
  const response = await runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: 'text/html' }, route: 'relay-only' });
  assertOk(response, { credential: false, what: `t.me/${name}`, now: runtime.clock.now() });
  // A channel without a public preview redirects to https://t.me/<name>.
  if (response.url && !/\/s\//.test(new URL(response.url).pathname)) {
    throw new DigesterError('unsupported', `@${name} has no public preview; account mode is needed for it.`);
  }
  return response;
}

export async function resolvePublic(address: string, runtime: SourceRuntime): Promise<SourceResolution<'telegram'>> {
  // Usernames are case-insensitive; the lower-cased form is the locator, the config and the URL.
  const name = parsePublicAddress(address)?.toLowerCase();
  if (!name) throw invalidInput('Enter a public channel as @name or t.me/name.');
  const response = await getPage(runtime, name);
  const page = await parsePublicPage(response.text, name);
  if (!page.title && page.posts.length === 0) throw new DigesterError('unsupported', `@${name} has no public preview; account mode is needed for it.`);
  return {
    kind: 'telegram',
    locator: `telegram:public:${name}`,
    name: page.title || `@${name}`,
    config: { kind: 'telegram', mode: 'public', username: name, peerType: 'channel' },
    requiredSecrets: [],
  };
}

export async function fetchPublic(ctx: SourceFetchContext<'telegram'>): Promise<SourceFetchResult> {
  const name = ctx.source.config.username;
  if (!name) throw new DigesterError('parse_failed', 'This public Telegram source has no channel name.');
  const lastId = typeof ctx.cursor?.lastId === 'number' ? ctx.cursor.lastId : 0;
  const collected = new Map<number, RawItem & { id: number }>();
  let before: number | undefined;
  // Read older pages only to close a gap since the last fetch (never on the first fetch).
  const maxPages = lastId > 0 ? Math.max(1, Math.ceil(ctx.maxItems / PAGE_POSTS)) : 1;
  for (let n = 0; n < maxPages; n++) {
    const response = await getPage(ctx.runtime, name, before);
    const page = await parsePublicPage(response.text, name);
    for (const p of page.posts) if (p.id > lastId) collected.set(p.id, p);
    const oldest = page.posts.reduce((min, p) => Math.min(min, p.id), Number.POSITIVE_INFINITY);
    if (lastId === 0 || !page.before || oldest <= lastId + 1 || page.posts.length === 0) break;
    before = page.before;
  }
  const newestFirst = [...collected.values()].sort((a, b) => b.id - a.id).slice(0, ctx.maxItems);
  const newest = newestFirst[0]?.id ?? lastId;
  const items: RawItem[] = newestFirst.map(({ id: _id, ...item }) => item);
  return { items, cursor: { ...(ctx.cursor ?? {}), lastId: Math.max(newest, lastId) } };
}
